import { useState } from "react";
import {
  useAddAdminCustomerSheetMutation,
  useForceDeleteAdminCustomerSheetMutation,
  useGetAdminCustomerSheetsQuery,
  useGetDeletedAdminCustomerSheetsQuery,
  useRestoreAdminCustomerSheetMutation,
} from "../../../store/apiSlices/adminCustomerSheets";
import { initialRowData } from "./data";
import {
  ArrowUTurnLeft,
  CrossIcon,
  LibraryIcon,
  SpinnerIcon,
  TrashIcon,
  UsersIcon,
} from "../../../components/Icons";
import CustomModal from "../../DealTracker/CustomModal";
import { parseServerDate, timeAgo } from "../../../utils";
import HoverExpandButton from "../../Shared/HoverExpandButton";

export default function CustomerSheetHeader() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isLoading: dealsTableLoading, data } =
    useGetAdminCustomerSheetsQuery();

  const { data: deletedData } = useGetDeletedAdminCustomerSheetsQuery();

  const [addSheet, { isLoading }] = useAddAdminCustomerSheetMutation();

  const hasPinned = data?.data?.some((t) => t.is_pin === 1);

  const payload = {
    title: "New Table",
    complete_json: [initialRowData],
  };

  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleAddNewTable = async () => {
    await addSheet(payload);
    if (hasPinned) {
      window.scroll({ top: 600, behavior: "smooth" });
    }
  };

  if (dealsTableLoading) return <TableActionSkeleton />;

  return (
    <div className="flex flex-wrap items-center justify-between">
      <div className="flex items-center gap-2 text-gray-800">
        <UsersIcon className="size-5" />
        <p className="font-medium">
          Customer sheets{" "}
          {data?.data && data?.data.length > 0
            ? `(${data?.data.length})`
            : null}
        </p>
      </div>
      <div className="flex justify-end gap-3 py-5">
        <HoverExpandButton
          label="Collapse Sheets"
          title="Close all open tables"
          colorClass="text-gray-800 text-sm bg-white hover:text-custom-blue-medium"
          className="!px-5 !py-2 !h-auto"
          hoverWidth="hover:w-[160px]"
          onClick={() =>
            window.dispatchEvent(new CustomEvent("close-all-tables"))
          }
        >
          <LibraryIcon className="size-5" />
        </HoverExpandButton>

        <button
          className="flex items-center gap-1 text-white text-sm bg-gray-400 px-5 py-2 rounded-xl hover:bg-gray-400/80"
          onClick={toggleModal}
        >
          <TrashIcon className="size-6" /> Recycle Bin{" "}
          {deletedData && deletedData.data.length > 0 && (
            <span className="w-4 h-4 bg-red-500 text-xs font-normal p-1 rounded-full flex items-center justify-center">
              {deletedData?.data.length}
            </span>
          )}
        </button>

        <button
          id="add-deal-tracker-table"
          className="flex items-center gap-1 bg-custom-blue-medium text-white text-sm px-5 py-2 rounded-xl shadow disabled:cursor-not-allowed hover:bg-custom-blue-medium/80"
          onClick={handleAddNewTable}
          disabled={isLoading}
        >
          {isLoading ? (
            <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
          ) : (
            "+"
          )}{" "}
          Add Sheet
        </button>
      </div>

      <CustomModal
        open={isModalOpen}
        onClose={toggleModal}
        className="bg-white rounded-xl shadow-xl p-6 max-w-xl w-full"
      >
        <button onClick={toggleModal} className="float-end hover:scale-95">
          <CrossIcon className="size-6" />
        </button>
        <div className="space-y-5">
          <h3 className="flex items-center gap-1 text-lg font-medium">
            <TrashIcon className="size-6" /> Recycle Bin
          </h3>

          <div className="space-y-3 h-[400px] overflow-y-scroll will-change-transform">
            {deletedData && deletedData.data.length > 0 ? (
              deletedData?.data.map((el, i) => (
                <div
                  key={el.id}
                  className="text-gray-900 flex justify-between items-start border-b py-1"
                >
                  <div className="flex items-center gap-3">
                    <p className="font-medium text-xl">{i + 1}</p>
                    <div>
                      <p className="text-sm font-medium">{el.title}</p>
                      <p className="text-xs text-gray-700">
                        {timeAgo(parseServerDate(el.deleted_at))}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <RestoreBtn tableId={el.id} />
                    <ForceDeleteBtn tableId={el.id} />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-gray-500">No data found</p>
            )}
          </div>
        </div>
      </CustomModal>
    </div>
  );
}

function RestoreBtn({ tableId }: { tableId: number }) {
  const [restoreTable, { isLoading: restoreLoading }] =
    useRestoreAdminCustomerSheetMutation();

  return (
    <button
      className="px-2 py-1 flex items-center gap-1 text-sm font-medium rounded-lg hover:bg-gray-200 hover:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
      onClick={() => restoreTable(tableId)}
      disabled={restoreLoading}
    >
      {restoreLoading ? (
        <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
      ) : (
        <ArrowUTurnLeft className="size-4" />
      )}
      Restore
    </button>
  );
}

function ForceDeleteBtn({ tableId }: { tableId: number }) {
  const [forceDelete, { isLoading }] =
    useForceDeleteAdminCustomerSheetMutation();

  const handleForceDelete = async () => {
    const { data } = await forceDelete(tableId);

    if (data.status === "success") {
      localStorage.removeItem(`columnOrder-${tableId}`);
    }
  };

  return (
    <button
      className="px-2 py-1 flex items-center gap-1 text-sm font-medium rounded-lg hover:bg-red-100 hover:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
      onClick={handleForceDelete}
      disabled={isLoading}
    >
      {isLoading ? (
        <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600" />
      ) : (
        <TrashIcon className="size-4" />
      )}
      Delete
    </button>
  );
}

function TableActionSkeleton() {
  return (
    <div className="flex justify-between items-center">
      <div className="animate-pulse h-5 w-[250px] bg-slate-300 rounded-xl" />

      <div className="flex justify-end gap-3 py-5">
        {[1, 2].map((el) => (
          <div
            key={el}
            className="animate-pulse h-10 w-[150px] bg-slate-300 rounded-xl"
          />
        ))}
      </div>
    </div>
  );
}
